import { Badge, Button } from 'components/common'
import { Table, TableCell, TableRow } from 'components/common/minimalTable'
import { KdsSync, OrderProductType, OrderType, PowerBiSync } from 'types/order'
import { convertTo12HourFormat } from 'utils/helpers'
import { HOLD_ACTIONS, ORDER_STATUS_CLASSES } from 'utils/constants'
import { roundToFourDecimals } from 'utils/helpers/round'
import { useState } from 'react'
import {
  unholdPriceDifferenceOrder,
  updateHoldOrderByReason,
} from 'utils/api/orderApi'
import Modal from 'components/modal'
import useModal from 'hooks/useModal'
import StoresDropdown from 'components/configuration/storesDropdown'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { handleError } from 'utils/helpers/handleError'
import { groupBundleProducts } from './utils'
import OrderSourceChip from './orderSourceChip'

const DELAYED_REFETCH_MS = 2000

const InfoRow = ({ label, value, highlight, className }: any) => (
  <div className="grid grid-cols-[200px_10px_1fr] items-start gap-2 py-0.5">
    <span className="text-[14px] font-medium text-gray-800">{label}</span>
    <span className="text-[14px] text-gray-400">:</span>
    <span
      className={`
        w-fit max-w-full break-words text-[14px] text-gray-600
        ${
          highlight ? 'rounded bg-red-50/50 px-1.5 text-red-600' : ''
        } ${className}
      `}
    >
      {value ?? 'N/A'}
    </span>
  </div>
)

type SectionCardProps = {
  title: string
  children: React.ReactNode
  className?: string
}

const SectionCard = ({ title, children, className }: SectionCardProps) => {
  return (
    <div
      className={`
        rounded-xl border border-gray-200 bg-white p-4 
        ${className || ''}
      `}
    >
      <p className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-gray-400">
        {title}
      </p>

      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  )
}

const ProductRow = ({
  _id,
  description,
  barcode,
  qty,
  systemPrice,
  unit_price_inclv_tax,
  amountExclVat,
  unitVat,
  uom,
  invalidBarcodes,
  orderId,
  isPriceDifferenceExceeded,
  isEditor,
  isGrubtechOrder,
  showKdsStatus,
  onRefetch,
  isBundlePriceMismatch = false,
  ...product
}: OrderProductType & {
  invalidBarcodes: string[]
  orderId: string
  isEditor: boolean
  isGrubtechOrder: boolean
  showKdsStatus?: boolean
  onRefetch: () => void
  // Line belongs to a bundle held for BUNDLE_PRICE_MISMATCH
  isBundlePriceMismatch?: boolean
}) => {
  const [isEditBarcode, setIsEditBarcode] = useState(false)
  const [isEditQtyPrice, setIsEditQtyPrice] = useState(false)
  const [newBarcode, setNewBarcode] = useState(barcode)
  const [newQty, setNewQty] = useState(qty)
  const [newPriceInclTax, setNewPriceInclTax] = useState(unit_price_inclv_tax)
  const [loading, setLoading] = useState(false)

  const originalPrice = product?.originalUnitPrice || 0
  const itemHasDiscount =
    originalPrice > 0 && unit_price_inclv_tax !== originalPrice

  const handleSaveBarcode = async () => {
    const toastId = toast.loading('Initiating barcode update...', {
      duration: Infinity,
    })
    setLoading(true)
    try {
      const payload = {
        reason: HOLD_ACTIONS.BARCODE_FIX,
        products: [{ newBarcode, productId: _id }],
      }
      await updateHoldOrderByReason(orderId, payload)
      toast.success('Barcode update initiated', { id: toastId, duration: 3000 })
      setIsEditBarcode(false)
      setTimeout(() => {
        setLoading(false)
        onRefetch()
      }, DELAYED_REFETCH_MS)
    } catch (error) {
      handleError(error, toastId, {
        DUPLICATE_BARCODE: 'This barcode already exists in the order',
      })
      setLoading(false)
    }
  }

  const handleSaveQtyPrice = async () => {
    const toastId = toast.loading('Initiating quantity & price update...', {
      duration: Infinity,
    })
    setLoading(true)
    try {
      const payload = {
        reason: HOLD_ACTIONS.PRODUCT_CHANGE,
        products: [
          { quantity: newQty, price: newPriceInclTax, productId: _id },
        ],
      }

      await updateHoldOrderByReason(orderId, payload)

      toast.success('Quantity & Price update initiated', {
        id: toastId,
        duration: 3000,
      })
      setIsEditQtyPrice(false)
      setTimeout(() => {
        setLoading(false)
        onRefetch()
      }, DELAYED_REFETCH_MS)
    } catch (error) {
      handleError(error, toastId)
      setLoading(false)
    }
  }

  const isBundleChild = Boolean(product?.isBundleChild)
  const isBundleParent = Boolean(product?.isBundleParent)

  const productBadges = [
    isBundleParent && (
      <Badge key="bundle" color="primary">
        Bundle
      </Badge>
    ),
    isBundleParent && isBundlePriceMismatch && (
      <Badge key="bundle-price-mismatch" color="red">
        Price Mismatch
      </Badge>
    ),
    product?.isAutoAttached && (
      <Badge key="auto-attached" color="teal">
        Auto Attached
      </Badge>
    ),
    product?.isComboAdjusted && isGrubtechOrder && (
      <Badge key="combo-adjusted" color="red">
        Combo Price Adjusted
      </Badge>
    ),
    product?.shouldIgnoreSyncToErp && (
      <Badge key="ignore-erp" color="gray">
        Ignore ERP
      </Badge>
    ),
  ].filter(Boolean)

  return (
    <TableRow
      key={_id}
      className={`
        transition-colors hover:bg-gray-50
        ${isPriceDifferenceExceeded ? 'bg-red-50 hover:bg-red-50' : ''}
        ${
          isBundleParent && isBundlePriceMismatch
            ? 'bg-red-50 hover:bg-red-50'
            : ''
        }
        ${
          isBundleChild && isBundlePriceMismatch
            ? 'bg-red-50/50 hover:bg-red-50/50'
            : ''
        }
        ${isBundleChild && !isBundlePriceMismatch ? 'bg-gray-50/70' : ''}
      `}
    >
      {/* Product Details */}
      <TableCell>
        <div className={isBundleChild ? 'flex items-start gap-2' : ''}>
          {isBundleChild && (
            <span className="mt-0.5 shrink-0 text-gray-300">↳</span>
          )}

          <div className="min-w-0">
            {isEditBarcode && isEditor ? (
              <input
                name="New Barcode"
                placeholder="New Barcode"
                value={newBarcode}
                onChange={(e) => setNewBarcode(e.target.value)}
                className="rounded-lg border px-2 py-1.5 outline-none focus:border-gray-600"
              />
            ) : (
              <>
                {productBadges.length > 1 && (
                  <p className="mb-1 flex flex-wrap items-center gap-1.5">
                    {productBadges}
                  </p>
                )}
                {description && (
                  <p className="mb-1 flex items-center gap-1.5 font-bold">
                    {productBadges.length === 1 && productBadges}
                    <span className="truncate">{description}</span>
                  </p>
                )}
                {(isBundleParent ||
                  !(isGrubtechOrder && product?.shouldIgnoreSyncToErp)) && (
                  <>
                    <p className="flex items-center gap-1.5 text-sm font-normal text-gray-800">
                      <span className="tracking-[1.5px]">{barcode}</span>
                      {uom && (
                        <>
                          <span className="text-gray-300">|</span>
                          <span>{uom}</span>
                        </>
                      )}
                    </p>
                    <p className="mt-1 text-sm font-light">ID: {_id}</p>
                  </>
                )}
                {product?.weight != null && (
                  <p className="mt-1 text-sm font-light text-gray-800">
                    Weight: {product.weight} kg
                  </p>
                )}
                {product?.comments && (
                  <p className="mt-1 w-fit rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800">
                    <strong className="font-semibold">Comment:</strong>{' '}
                    {product.comments}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </TableCell>

      {/* Quantity Column */}
      <TableCell>
        {isEditQtyPrice && isEditor ? (
          <input
            name="qty"
            type="number"
            placeholder="New Quantity"
            value={newQty}
            onChange={(e) => setNewQty(Number(e.target.value))}
            className="w-20 rounded-lg border px-2 py-1.5 outline-none focus:border-gray-600"
          />
        ) : (
          <p>{qty}</p>
        )}
      </TableCell>

      <TableCell>{roundToFourDecimals(systemPrice) ?? 'NA'}</TableCell>

      {/* Price Including Tax Column (Editable) */}
      <TableCell>
        {isEditQtyPrice && isEditor ? (
          <input
            name="price"
            type="number"
            step="0.01"
            placeholder="New Price Incl. Tax"
            value={newPriceInclTax}
            onChange={(e) => setNewPriceInclTax(Number(e.target.value))}
            className="w-24 rounded-lg border px-2 py-1.5 outline-none focus:border-gray-600"
          />
        ) : (
          <>
            {itemHasDiscount && !isGrubtechOrder && (
              <span className="text-xs text-gray-500 line-through">
                {originalPrice}
              </span>
            )}

            <span
              className={
                itemHasDiscount && !isGrubtechOrder
                  ? 'ml-1 text-sm font-bold'
                  : ''
              }
            >
              {roundToFourDecimals(
                isGrubtechOrder ? originalPrice : unit_price_inclv_tax,
              ) ?? 'NA'}
            </span>
          </>
        )}
      </TableCell>
      {/* Unit Price Excl Tax */}
      {!isGrubtechOrder && (
        <TableCell className="text-right">
          <div className="flex flex-col gap-1">
            <div>
              <span className="text-xs text-gray-500">Ext</span>
              <span className="ml-1 font-medium text-gray-800">
                {roundToFourDecimals(product?.unit_price_excl_tax)}
              </span>
            </div>
            <div>
              <span className="text-xs text-gray-500">Calc.</span>
              <span className="ml-1 font-semibold text-blue-600">
                {roundToFourDecimals(amountExclVat) ?? 'NA'}
              </span>
            </div>
          </div>
        </TableCell>
      )}

      {/* Unit VAT */}
      <TableCell className="text-right">
        <div className="flex flex-col gap-1">
          <div>
            <span className="text-xs text-gray-500">Ext</span>
            <span className="ml-1 font-medium text-gray-800">
              {qty > 0
                ? roundToFourDecimals(Number(product?.vat_amount) / qty)
                : 0}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-500">Calc.</span>
            <span className="ml-1 font-semibold text-blue-600">
              {unitVat !== undefined && unitVat !== null
                ? roundToFourDecimals(unitVat) ?? 0
                : 'NA'}
            </span>
          </div>
        </div>
      </TableCell>

      {/* Total VAT */}
      <TableCell className="text-right">
        <div className="flex flex-col gap-1">
          <div>
            <span className="text-xs text-gray-500">Ext</span>
            <span className="ml-1 font-medium text-gray-800">
              {roundToFourDecimals(product?.vat_amount)}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-500">Calc.</span>
            <span className="ml-1 font-semibold text-blue-600">
              {unitVat !== undefined && unitVat !== null
                ? roundToFourDecimals(unitVat * qty) ?? 0
                : 'NA'}
            </span>
          </div>
        </div>
      </TableCell>

      {/* Row Total */}
      <TableCell className="text-right">
        <div className="flex flex-col gap-1">
          <div>
            <span className="text-xs text-gray-500">Ext</span>
            <span className="ml-1 font-medium text-gray-800">
              {product?.row_total}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-500">Ext Cal.</span>
            <span className="ml-1 font-semibold text-blue-600">
              {roundToFourDecimals(Number(unit_price_inclv_tax) * qty)}
            </span>
          </div>
          {product?.rowDiscountAmount && product.rowDiscountAmount > 0 ? (
            <div>
              <span className="text-xs text-gray-500">Discount</span>
              <span className="ml-1 font-semibold text-green-600">
                {roundToFourDecimals(product.rowDiscountAmount)}
              </span>
            </div>
          ) : null}
        </div>
      </TableCell>

      {/* KDS Status Column */}
      {showKdsStatus && (
        <TableCell>{product?.kdsRemoteStatus || '-'}</TableCell>
      )}

      {/* Actions Column */}
      {isEditor && (
        <TableCell>
          {/* Barcode Editing Buttons */}
          {invalidBarcodes.includes(barcode) ? (
            isEditBarcode ? (
              <div className="flex items-center">
                <Button
                  buttonText={loading ? 'Saving...' : 'Save'}
                  type="button"
                  className="mr-2"
                  disabled={loading}
                  onClick={handleSaveBarcode}
                />
                <Button
                  buttonText="Cancel"
                  type="button"
                  variant="outline"
                  disabled={loading}
                  onClick={() => setIsEditBarcode(false)}
                />
              </div>
            ) : (
              <Button
                buttonText="Edit Barcode"
                type="button"
                onClick={() => setIsEditBarcode(true)}
              />
            )
          ) : (
            <>
              {/* Quantity + Price Editing Buttons (Only if price difference exceeded) */}
              {isPriceDifferenceExceeded ? (
                isEditQtyPrice ? (
                  <div className="flex items-center">
                    <Button
                      buttonText={loading ? 'Saving...' : 'Save'}
                      type="button"
                      className="mr-2"
                      disabled={loading}
                      onClick={handleSaveQtyPrice}
                    />
                    <Button
                      buttonText="Cancel"
                      type="button"
                      variant="outline"
                      disabled={loading}
                      onClick={() => setIsEditQtyPrice(false)}
                    />
                  </div>
                ) : (
                  <Button
                    buttonText="Edit Qty & Price"
                    type="button"
                    onClick={() => setIsEditQtyPrice(true)}
                  />
                )
              ) : (
                ''
              )}
            </>
          )}
        </TableCell>
      )}
    </TableRow>
  )
}

const formatDisplayDate = (order: OrderType, date?: string) => {
  if (!date) return 'N/A'

  // UTC →  local
  return convertTo12HourFormat(date)
}

const HoldReasons = ({
  holdReasons = [],
  order,
}: {
  holdReasons: string[]
  order: OrderType
}) => {
  if (!holdReasons.length) return null

  return (
    <div className="mt-4 space-y-2">
      {holdReasons.includes('STORE_INVALID') && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
          Invalid Store
        </div>
      )}

      {holdReasons.includes('BARCODE_INVALID') && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2">
          <div className="text-sm font-semibold text-yellow-800">
            Invalid Barcodes
          </div>
          {order?.invalidBarcodes?.length > 0 && (
            <ul className="mt-1 list-disc pl-4 text-xs text-gray-700">
              {order.invalidBarcodes.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {holdReasons.includes('BARCODE_DISABLED') && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2">
          <div className="text-sm font-semibold text-yellow-800">
            Disabled Barcodes
          </div>
          {order?.disabledBarcodes?.length > 0 && (
            <ul className="mt-1 list-disc pl-4 text-xs text-gray-700">
              {order.disabledBarcodes.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {holdReasons.includes('HUGE_PRICE_DIFF') && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm font-semibold text-yellow-800">
          Huge Price Difference (Item)
        </div>
      )}

      {holdReasons.includes('HUGE_PRICE_DIFF_TOTAL') && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm font-semibold text-yellow-800">
          Huge Price Difference (Total)
        </div>
      )}

      {holdReasons.includes('BUNDLE_PRICE_MISMATCH') && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2">
          <div className="text-sm font-semibold text-red-700">
            Bundle Price Mismatch
          </div>
          <p className="mt-1 text-xs text-gray-700">
            Bundle price doesn&apos;t match the total of its items. This order
            won&apos;t be sent to SAP.
          </p>
          {!!order?.bundlePriceMismatchBarcodes?.length && (
            <ul className="mt-1 list-disc pl-4 text-xs text-gray-700">
              {order.bundlePriceMismatchBarcodes.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {holdReasons.includes('PENDING_ITEM_REPLACEMENT') && (
        <div className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-2">
          <div className="text-sm font-semibold text-blue-800">
            Pending Item Replacement
          </div>
          <p className="mt-1 text-xs text-gray-700">
            Waiting for the store app to finish the item-replacement cycle for
            this order. Nothing is sent to SAP or the last mile until a picker
            confirms it.
          </p>
        </div>
      )}
    </div>
  )
}

const PowerBiSyncCard = ({ sync }: { sync?: PowerBiSync }) => {
  const statusBadge = () => {
    if (!sync) {
      return (
        <span className="rounded-lg bg-gray-200 px-2 py-0.5 text-sm font-semibold text-gray-600">
          N/A
        </span>
      )
    }
    if (sync.syncStatus === 'SYNCED') {
      return (
        <span className="rounded-lg border border-green-300 px-2 py-0.5 text-sm font-semibold text-green-700">
          Synced
        </span>
      )
    }
    return (
      <span className="rounded-lg border border-yellow-400 px-2 py-0.5 text-sm font-semibold text-yellow-700">
        Pending
      </span>
    )
  }

  const eventTypeLabel = () => {
    if (!sync?.eventType) return '—'
    return sync.eventType === 'CREATED' ? 'Created' : 'Canceled'
  }

  const lastSyncedLabel = () => {
    if (!sync?.lastSyncedAt) return '—'
    return convertTo12HourFormat(sync.lastSyncedAt)
  }

  return (
    <SectionCard title="Power BI Sync">
      <div className="grid grid-cols-[200px_10px_1fr] items-start gap-2 py-0.5">
        <span className="text-[14px] font-medium text-gray-800">
          Sync Status
        </span>
        <span className="text-[14px] text-gray-400">:</span>
        <span>{statusBadge()}</span>
      </div>
      <InfoRow label="Event Type" value={eventTypeLabel()} />
      <InfoRow label="Last Synced At" value={lastSyncedLabel()} />
    </SectionCard>
  )
}

const KdsSyncCard = ({ sync }: { sync?: KdsSync }) => {
  if (!sync || !sync?.isKdsEligible) return null

  const statusBadge = () => {
    if (sync.syncStatus === 'SYNCED') {
      return (
        <span className="rounded-lg border border-green-300 px-2 py-0.5 text-sm font-semibold text-green-700">
          Synced
        </span>
      )
    }
    if (sync.syncStatus === 'FAILED') {
      return (
        <span className="rounded-lg border border-red-300 px-2 py-0.5 text-sm font-semibold text-red-700">
          Failed
        </span>
      )
    }
    return (
      <span className="rounded-lg border border-yellow-400 px-2 py-0.5 text-sm font-semibold text-yellow-700">
        Pending
      </span>
    )
  }

  const lastAttemptLabel = () => {
    if (!sync.lastAttemptAt) return '—'
    return convertTo12HourFormat(sync.lastAttemptAt)
  }

  return (
    <SectionCard title="KDS Sync">
      <InfoRow label="Action" value={sync.action} />
      <div className="grid grid-cols-[200px_10px_1fr] items-start gap-2 py-0.5">
        <span className="text-[14px] font-medium text-gray-800">
          Sync Status
        </span>
        <span className="text-[14px] text-gray-400">:</span>
        <span>{statusBadge()}</span>
      </div>
      {sync.lastError && (
        <p className="mt-0.5 text-[13px] font-medium text-red-600">
          {sync.lastError}
        </p>
      )}
      <InfoRow label="Last Attempt At" value={lastAttemptLabel()} />
      {sync.remoteStatus && (
        <InfoRow label="Remote Status" value={sync.remoteStatus} />
      )}
    </SectionCard>
  )
}

const OrderDetailsSection = ({
  order,
  isEditor,
  onRefetch,
}: {
  order: OrderType
  isEditor: boolean
  onRefetch: () => void
}) => {
  const {
    order_no,
    Order_shortcode,
    transNo,
    Store_code,
    order_place_datetime,
    Planned_DeliveryDate,
    Delivery_provider,
    order_value_inclv_tax,
    order_value_excluv_tax,
    vat_amount,
    service_fee,
    delivery_fee,
    customer_Name,
    customer_Address,
    customer_Address2,
    Area,
    City,
    Contact,
    products = [],
    payment = [],
    Country,
    invoiceNo,
    storeName,
    Aggregator_Code,
    Aggregator_Name,
    lastMile,
    totalAmountExclVat,
    totalAmountInclVat,
    totalVat,
    createdAt,
    status,
    Prepared_by,
    empId,
    pickerId,
    pickerName,
    cancelledByPickerId,
    longitude,
    latitude,
    invalidBarcodes,
    holdReasons,
    onHold,
    lastMileStatus,
  } = order
  const { closeModal, isOpen, openModal } = useModal()
  const { setValue, handleSubmit, getValues } = useForm()
  const [isUnholding, setIsUnholding] = useState(false)
  // const navigate = useNavigate()

  const canUnholdPriceDifference =
    onHold &&
    (holdReasons.includes('HUGE_PRICE_DIFF') ||
      holdReasons.includes('HUGE_PRICE_DIFF_TOTAL'))

  const handleUnholdPriceDifference = async () => {
    const toastId = toast.loading('Removing hold...', { duration: Infinity })
    setIsUnholding(true)
    try {
      await unholdPriceDifferenceOrder(order.order_no)
      toast.success('Order hold removed', { id: toastId, duration: 3000 })
      onRefetch()
    } catch (error) {
      handleError(error, toastId)
    } finally {
      setIsUnholding(false)
    }
  }

  const onSubmit = async () => {
    const selectedStoreId = getValues('stores')
    if (!selectedStoreId) {
      toast.error('No store selected.')

      // Show validation error, toast, or early return
      return
    }
    const payload = {
      storeCode: selectedStoreId,
      reason: HOLD_ACTIONS.STORE_FIX,
    }

    await updateHoldOrderByReason(order.order_no, payload)
    // navigate(-1)
    toast.success('order will be updated')

    closeModal()
    setTimeout(onRefetch, DELAYED_REFETCH_MS)
  }

  const formatHoldDuration = (ms: any) => {
    if (!ms && ms !== 0) return 'N/A'

    const totalSeconds = Math.floor(ms / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60

    // padStart ensures 2-digit formatting (e.g., 05:09)
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
      2,
      '0',
    )}`
  }

  const isTotalAmountExceeded =
    holdReasons?.includes('HUGE_PRICE_DIFF_TOTAL') ?? false

  // Bundle groups held for price mismatch - only while the hold reason is active
  const mismatchedBundleGroupIds = new Set(
    holdReasons?.includes('BUNDLE_PRICE_MISMATCH')
      ? products
          .filter(
            (p) =>
              p?.isBundleParent &&
              p?.bundleGroupId &&
              order?.bundlePriceMismatchBarcodes?.includes(p.barcode),
          )
          .map((p) => p.bundleGroupId)
      : [],
  )

  const hasKdsStatus = products.some((product) => product.kdsRemoteStatus)

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          {/* Order Number Container */}
          <p className="rounded-lg bg-slate-900 px-3 py-1 font-mono text-lg font-bold  text-white">
            {order_no}
          </p>

          <OrderSourceChip
            isPosOrder={order?.isPosOrder}
            isGrubtechOrder={order?.isGrubtechOrder}
            aggregatorCode={order?.Aggregator_Code}
            aggregatorName={order?.Aggregator_Name}
          />
        </div>
        <div className="flex flex-row-reverse items-center gap-2">
          {isEditor && holdReasons.includes('STORE_INVALID') && onHold && (
            <Button onClick={openModal} buttonText="Store Edit" />
          )}
          {isEditor && canUnholdPriceDifference && (
            <Button
              onClick={handleUnholdPriceDifference}
              buttonText="Unhold Order"
              loading={isUnholding}
            />
          )}
        </div>
        {isEditor && holdReasons.includes('STORE_INVALID') && onHold && (
          <Modal
            width={480}
            title={`Valid Stores`}
            titleClassname="text-xl font-semibold max-w-xl"
            isOpen={isOpen}
            closeModal={closeModal}
            customStyle={{
              padding: '25px 40px',
            }}
            height={450}
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="flex flex-col  justify-between gap-52">
                <StoresDropdown
                  label="Available stores"
                  name="stores"
                  setValue={setValue}
                  withNoneOption
                  required
                />
                <Button buttonText="Submit" type="submit">
                  Submit
                </Button>
              </div>
            </form>
          </Modal>
        )}
      </div>

      {order?.cancelReason && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
          <div className="mb-2 flex items-center gap-2 border-b border-gray-100 pb-2 text-red-600">
            <span className="text-xs font-bold uppercase tracking-tight">
              Cancellation Details
            </span>
          </div>
          <div className="space-y-2">
            <p className="text-xs text-gray-500">{order?.cancelBy || 'N/A'}</p>
            <p className="text-xs italic leading-snug text-gray-500">
              <strong className="not-italic text-gray-800">Reason: </strong>
              {order?.cancelReason || 'No reason provided'}
            </p>
          </div>
        </div>
      )}

      <HoldReasons holdReasons={holdReasons} order={order} />

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* LEFT SIDE */}
        <div className="flex flex-col gap-4">
          {/* Order Info */}
          <SectionCard title="Order Info">
            <InfoRow
              className={`w-max rounded-lg px-2 py-0.5 font-semibold ${
                ORDER_STATUS_CLASSES[status!]
              }`}
              label="Order Status"
              value={status}
            />
            {lastMileStatus && (
              <InfoRow label="Last Mile Status" value={lastMileStatus} />
            )}
            <InfoRow label="Invoice No" value={invoiceNo} />
            <InfoRow label="Order Shortcode" value={Order_shortcode} />
            <InfoRow label="Trans No" value={transNo} />

            <InfoRow label="Prepared By" value={Prepared_by} />
            <InfoRow label="Employee ID" value={empId} />
            <InfoRow label="Picker ID" value={pickerId} />
            {pickerName && <InfoRow label="Picker Name" value={pickerName} />}
            {cancelledByPickerId && (
              <InfoRow
                label="Voided By (Picker ID)"
                value={cancelledByPickerId}
              />
            )}
            <InfoRow label="Delivery Provider" value={Delivery_provider} />
            <InfoRow
              label="Coordinates"
              value={
                latitude || longitude
                  ? `${latitude ?? 'N/A'}, ${longitude ?? 'N/A'}`
                  : 'N/A'
              }
            />
          </SectionCard>

          {/* Store & Aggregator */}
          <SectionCard title="Store & Aggregator">
            <InfoRow label="Store Name" value={storeName} />
            <InfoRow label="Store Code" value={Store_code} />
            <InfoRow label="Aggregator Name" value={Aggregator_Name} />
            <InfoRow label="Aggregator Code" value={Aggregator_Code} />
            {order?.deliverooStatus && (
              <InfoRow
                label="Deliveroo Status"
                value={order.deliverooStatus}
                className="rounded-full border border-orange-200 bg-orange-50 px-2.5 font-medium text-orange-700"
              />
            )}
          </SectionCard>

          <SectionCard title="Customer Details">
            <InfoRow label="Name" value={customer_Name} />
            <InfoRow label="Contact" value={Contact} />
            <InfoRow label="Address 1" value={customer_Address || 'N/A'} />
            <InfoRow label="Address 2" value={customer_Address2} />
            <InfoRow label="Area" value={Area} />
            <InfoRow label="City" value={City || 'N/A'} />
            <InfoRow label="Country" value={Country} />
            {order?.customerInstructions && (
              <InfoRow
                label="Customer Instruction"
                value={order.customerInstructions}
              />
            )}
          </SectionCard>

          {order?.isGrubtechOrder && (
            <SectionCard title="Grubtech">
              <InfoRow label="Order No" value={order?.grubtechOrderNo} />
              <InfoRow label="Order Type" value={order?.grubtechOrderType} />
              <InfoRow label="Brand Name" value={order?.brandName} />
              <InfoRow label="Brand Id" value={order?.brandId} />
            </SectionCard>
          )}

          {/* KDS Sync */}
          <KdsSyncCard sync={order?.kdsSync} />
        </div>

        {/* RIGHT SIDE */}
        <div className="flex flex-col gap-4">
          {/* Dates */}

          <SectionCard title="Dates">
            <InfoRow
              label={`Order Date`}
              value={formatDisplayDate(order, order_place_datetime)}
            />
            <InfoRow
              label={`Planned Delivery`}
              value={formatDisplayDate(order, Planned_DeliveryDate)}
            />
            <InfoRow
              label="Int. Order Posting Date"
              value={createdAt ? convertTo12HourFormat(createdAt) : 'N/A'}
            />
          </SectionCard>

          {/* Financials */}
          <SectionCard title="Financials">
            <InfoRow
              label="Order Value (Incl. Tax)"
              value={roundToFourDecimals(order_value_inclv_tax)}
              highlight={isTotalAmountExceeded}
            />
            <InfoRow
              label="Calc. Order Value (Incl. Tax)"
              value={roundToFourDecimals(totalAmountInclVat)}
            />
            <InfoRow
              label="Order Value (Excl. Tax)"
              value={roundToFourDecimals(order_value_excluv_tax)}
            />
            <InfoRow
              label="Calc. Order Value (Excl. Tax)"
              value={roundToFourDecimals(totalAmountExclVat)}
            />
            <InfoRow
              label="VAT Amount"
              value={roundToFourDecimals(vat_amount)}
            />
            <InfoRow
              label="Calc. VAT Amount"
              value={roundToFourDecimals(totalVat)}
            />

            {order?.totalDiscountAmount && order.totalDiscountAmount > 0 ? (
              <InfoRow
                label="Total Discount"
                className="font-semibold text-green-700"
                value={roundToFourDecimals(order.totalDiscountAmount)}
              />
            ) : null}

            <InfoRow
              label="Service Fee"
              value={roundToFourDecimals(service_fee)}
            />
            <InfoRow
              label="Delivery Fee"
              value={roundToFourDecimals(delivery_fee)}
            />
          </SectionCard>

          {/* Status */}
          <SectionCard title="Status">
            <InfoRow label="Last Mile" value={lastMile} />

            <InfoRow
              className={`rounded-lg px-2 py-0.5 font-semibold ${
                order?.lastMileSync
                  ? 'border border-green-300  text-green-700'
                  : 'border border-red-300  text-red-700'
              }`}
              label="Last Mile Sync"
              value={order?.lastMileSync ? 'Success' : 'Failed'}
            />
            <InfoRow
              className={`rounded-md px-2 py-0.5 font-semibold ${
                order?.erpSync
                  ? 'border border-green-300  text-green-700'
                  : 'border border-red-300  text-red-700'
              }`}
              label="ERP Sync"
              value={order?.erpSync ? 'Success' : 'Failed'}
            />

            <InfoRow
              className={`rounded-lg px-2 py-0.5 font-semibold ${
                order?.onHold
                  ? 'bg-yellow-300 text-black'
                  : 'bg-gray-300 text-white'
              }`}
              label="On Hold"
              value={order?.onHold ? 'True' : 'False'}
            />

            <InfoRow
              className={`rounded-lg px-2 py-0.5 font-semibold ${
                order?.isDraft
                  ? 'bg-yellow-300 text-black'
                  : 'bg-gray-300 text-white'
              }`}
              label="Is Draft?"
              value={order?.isDraft ? 'Yes' : 'No'}
            />

            <InfoRow
              className={`rounded-lg px-2 py-0.5 font-semibold ${
                order?.totalHoldDuration
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-300 text-white'
              }`}
              label="POS Hold Duration"
              value={formatHoldDuration(order?.totalHoldDuration)}
            />
          </SectionCard>

          {/* Power BI Sync */}
          <PowerBiSyncCard sync={order?.powerBiSync} />
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <h3 className="title">Products</h3>
        {products?.length > 0 && (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-500">
            {products.length}
          </span>
        )}
      </div>
      {products?.length > 0 ? (
        <Table
          headers={[
            'Product',
            'Quantity',
            'Grandiose Price',
            'Unit Price (Incl. Tax)',
            ...(order?.isGrubtechOrder ? [] : ['Unit Price (Excl. Tax)']),
            'Unit VAT',
            'Total VAT',
            'Total',
            ...(hasKdsStatus ? ['KDS Status'] : []),
            ...(isEditor ? ['Action'] : []),
          ]}
        >
          {groupBundleProducts(products).map((product) => (
            <ProductRow
              key={product._id}
              {...product}
              isEditor={isEditor}
              invalidBarcodes={invalidBarcodes}
              orderId={order.order_no}
              isGrubtechOrder={order?.isGrubtechOrder ?? false}
              showKdsStatus={hasKdsStatus}
              onRefetch={onRefetch}
              isBundlePriceMismatch={
                !!product.bundleGroupId &&
                mismatchedBundleGroupIds.has(product.bundleGroupId)
              }
            />
          ))}
        </Table>
      ) : (
        <p className="text-sm text-gray-600">No Product details available</p>
      )}

      <h3 className="title mt-3">Payments</h3>
      {payment?.length > 0 ? (
        <Table headers={['Payment Type', 'Amount']}>
          {payment.map((pay) => (
            <TableRow key={pay._id}>
              <TableCell>{pay.payment_type}</TableCell>
              <TableCell>{pay.amount?.toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </Table>
      ) : (
        <p className="text-sm text-gray-600">No Payment details available</p>
      )}
    </>
  )
}

export default OrderDetailsSection
